import { z } from 'zod';

export const PROTOCOL_VERSION = 1;

const Envelope = <T extends string, P extends z.ZodTypeAny>(type: T, payload: P) =>
  z.object({
    v: z.literal(PROTOCOL_VERSION),
    type: z.literal(type),
    id: z.string(),
    payload,
  });

const Rect = z.object({
  x: z.number(),
  y: z.number(),
  width: z.number(),
  height: z.number(),
});

// Iframe → host
export const ReadyMessage = Envelope('ready', z.object({ runtimeVersion: z.string() }));
export const ElementClickedMessage = Envelope(
  'element-clicked',
  z.object({ selector: z.string(), snippet: z.string(), rect: Rect }),
);
export const TextEditedMessage = Envelope(
  'text-edited',
  z.object({ selector: z.string(), oldText: z.string(), newText: z.string() }),
);
export const ErrorMessage = Envelope(
  'error',
  z.object({ message: z.string(), stack: z.string().optional() }),
);

export const IframeMessage = z.discriminatedUnion('type', [
  ReadyMessage,
  ElementClickedMessage,
  TextEditedMessage,
  ErrorMessage,
]);
export type IframeMessage = z.infer<typeof IframeMessage>;

export function parseIframeMessage(data: unknown): IframeMessage | null {
  const result = IframeMessage.safeParse(data);
  return result.success ? result.data : null;
}

// Host → iframe
export type CanvasMode = 'select' | 'comment' | 'edit' | 'tweaks';

export type HostMessage =
  | { v: 1; type: 'patch-append'; id: string; payload: { chunk: string } }
  | {
      v: 1;
      type: 'apply-tweaks';
      id: string;
      payload: { values: Array<{ variable: string; value: string }> };
    }
  | {
      v: 1;
      type: 'apply-edits';
      id: string;
      payload: { edits: Array<{ selector: string; newText: string }> };
    }
  | { v: 1; type: 'enter-mode'; id: string; payload: { mode: CanvasMode } }
  | { v: 1; type: 'highlight'; id: string; payload: { selector: string | null } };

export function makeHostMessage<T extends HostMessage['type']>(
  type: T,
  payload: Extract<HostMessage, { type: T }>['payload'],
): Extract<HostMessage, { type: T }> {
  return {
    v: PROTOCOL_VERSION,
    type,
    id:
      typeof crypto !== 'undefined' && 'randomUUID' in crypto
        ? crypto.randomUUID()
        : `m-${Math.random().toString(36).slice(2)}`,
    payload,
  } as Extract<HostMessage, { type: T }>;
}
